// Outils de la barre de l'éditeur : liens, lien interne, glossaire, vidéo, formule, couleurs, tableau
import { useEffect, useMemo, useRef, useState } from "react";
import katex from "katex";
import "katex/dist/katex.min.css";
import { isValidYoutubeUrl } from "@tiptap/extension-youtube";
import {
  BookA, Columns3, FileText, Highlighter, Link as LinkIcon, Link2, Palette, Rows3, Search, Sigma, Table2,
  Trash2, Youtube,
} from "lucide-react";
import { toast } from "sonner";

const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

/** Bouton de la barre d'outils. */
export function ToolbarBtn({ onClick, active, disabled, title, children, testid, className = "" }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      data-testid={testid}
      className={`w-8 h-8 grid place-items-center rounded transition-colors ${
        active ? "bg-emerald-500/20 text-emerald-300" : "text-zinc-400 hover:bg-white/5 hover:text-zinc-200"
      } disabled:opacity-40 ${className}`}
    >
      {children}
    </button>
  );
}

/** Bouton + panneau qui s'ouvre dessous (fermé au clic à l'extérieur ou avec Échap). */
export function ToolPopover({ icon, title, active, testid, width = 300, onOpen, children, open: openProp, setOpen: setOpenProp }) {
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = setOpenProp ?? setOpenState;
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open, setOpen]);
  return (
    <div className="relative" ref={ref}>
      <ToolbarBtn
        title={title}
        active={active || open}
        testid={testid}
        onClick={() => {
          if (!open && onOpen && onOpen() === false) return;
          setOpen(!open);
        }}
      >
        {icon}
      </ToolbarBtn>
      {open && (
        <div className="absolute z-50 top-full mt-1 left-0 rounded-lg bg-[#0F0F10] border border-white/10 shadow-xl p-3" style={{ width }} data-testid={testid ? `${testid}-pop` : undefined}>
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

const Field = (props) => (
  <input {...props} className={`w-full h-9 rounded-md bg-white/[0.04] border border-white/10 px-2.5 text-sm focus:outline-none focus:border-emerald-500/50 ${props.className || ""}`} />
);

const SmallBtn = ({ children, variant = "primary", ...p }) => (
  <button
    type="button"
    {...p}
    className={`h-8 px-3 rounded-md text-xs font-medium ${
      variant === "primary" ? "bg-emerald-500 text-black hover:bg-emerald-300" : variant === "danger" ? "text-rose-300 hover:bg-rose-500/10" : "border border-white/10 text-zinc-300 hover:bg-white/5"
    } disabled:opacity-40`}
  >
    {children}
  </button>
);

/** Lien vers un site extérieur. */
export function LinkTool({ editor }) {
  const [url, setUrl] = useState("");
  const attrs = editor.getAttributes("link");
  const isExternal = editor.isActive("link") && !attrs.internal && !attrs.glossary;
  return (
    <ToolPopover
      icon={<LinkIcon className="w-4 h-4" />}
      title="Lien vers un autre site"
      active={isExternal}
      testid="rt-link"
      onOpen={() => setUrl(isExternal ? attrs.href || "" : "https://")}
    >
      {(close) => {
        const apply = () => {
          const href = url.trim();
          if (!/^https?:\/\/.+\..+/.test(href)) { toast.error("Adresse invalide (elle doit commencer par https://)"); return; }
          const chain = editor.chain().focus().extendMarkRange("link");
          if (editor.state.selection.empty && !editor.isActive("link")) chain.insertContent({ type: "text", text: href, marks: [{ type: "link", attrs: { href } }] }).run();
          else chain.setLink({ href, internal: null, glossary: null }).run();
          close();
        };
        return (
          <div className="space-y-2">
            <div className="text-xs text-zinc-500">Lien vers un autre site (s'ouvre dans un nouvel onglet)</div>
            <Field autoFocus value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), apply())} placeholder="https://…" />
            <div className="flex justify-between">
              {isExternal ? <SmallBtn variant="danger" onClick={() => { editor.chain().focus().extendMarkRange("link").unsetLink().run(); close(); }}>Retirer le lien</SmallBtn> : <span />}
              <SmallBtn onClick={apply}>Appliquer</SmallBtn>
            </div>
          </div>
        );
      }}
    </ToolPopover>
  );
}

const KIND = {
  course: { label: "Cours", cls: "text-orange-300 border-orange-500/30 bg-orange-500/10" },
  article: { label: "Analyse", cls: "text-violet-300 border-violet-500/30 bg-violet-500/10" },
};

/**
 * Lien vers un autre contenu du site, choisi par son titre.
 * targets : [{ key: "course:id" | "article:id", title, href, kind: "course" | "article", sub? }]
 */
export function InternalLinkTool({ editor, targets = [] }) {
  const [q, setQ] = useState("");
  const current = editor.getAttributes("link").internal;
  const list = useMemo(() => {
    const n = norm(q.trim());
    return targets.filter((t) => !n || norm(t.title).includes(n)).slice(0, 8);
  }, [q, targets]);
  return (
    <ToolPopover
      icon={<Link2 className="w-4 h-4" />}
      title="Lien vers un cours ou une analyse du site"
      active={!!current}
      testid="rt-internal"
      width={340}
      onOpen={() => {
        const { from, to } = editor.state.selection;
        setQ(editor.state.doc.textBetween(from, to, " ").trim());
      }}
    >
      {(close) => {
        const pick = (t) => {
          const attrs = { href: t.href, internal: t.key, glossary: null };
          if (editor.state.selection.empty && !editor.isActive("link")) {
            editor.chain().focus().insertContent({ type: "text", text: t.title, marks: [{ type: "link", attrs }] }).run();
          } else {
            editor.chain().focus().extendMarkRange("link").setLink(attrs).run();
          }
          close();
        };
        return (
          <div>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
              <Field autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Chercher un cours, une analyse…" className="pl-8" />
            </div>
            <div className="mt-2 max-h-64 overflow-y-auto -mx-1">
              {list.map((t) => (
                <button key={t.key} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(t)} className={`w-full text-left px-2 py-1.5 rounded flex items-center gap-2 hover:bg-white/5 ${t.key === current ? "bg-white/5" : ""}`}>
                  <span className={`shrink-0 text-[9px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded border ${KIND[t.kind]?.cls}`}>{KIND[t.kind]?.label}</span>
                  <span className="min-w-0">
                    <span className="block text-sm text-zinc-200 truncate">{t.title}</span>
                    {t.sub && <span className="block text-[11px] text-zinc-500 truncate">{t.sub}</span>}
                  </span>
                </button>
              ))}
              {!list.length && <div className="px-2 py-2 text-xs text-zinc-500">Aucun contenu trouvé.</div>}
            </div>
            <div className="mt-2 pt-2 border-t border-white/5 flex items-center justify-between gap-2">
              <span className="text-[11px] text-zinc-500 leading-snug">Sans texte sélectionné, le titre du contenu est inséré.</span>
              {current && <SmallBtn variant="danger" onClick={() => { editor.chain().focus().extendMarkRange("link").unsetLink().run(); close(); }}>Retirer</SmallBtn>}
            </div>
          </div>
        );
      }}
    </ToolPopover>
  );
}

/** Lien vers une définition du glossaire (sur le texte sélectionné). terms : [{ id, slug, title }] */
export function GlossaryTool({ editor, terms }) {
  const [q, setQ] = useState("");
  const current = editor.getAttributes("link").glossary;
  const n = norm(q);
  const list = terms
    .filter((t) => !n || norm(t.title).includes(n) || n.includes(norm(t.title.replace(/\s*\(.*\)/, ""))))
    .sort((a, b) => a.title.localeCompare(b.title, "fr"))
    .slice(0, 8);
  return (
    <div className="relative">
      <ToolPopover
        icon={<span className="inline-flex items-center gap-1"><BookA className="w-3.5 h-3.5" /></span>}
        title="Lier le texte sélectionné à une définition du glossaire"
        active={!!current}
        testid="rt-glossary"
        width={288}
        onOpen={() => {
          if (editor.state.selection.empty && !current) {
            toast.error("Sélectionnez d'abord un mot ou une expression dans le texte");
            return false;
          }
          const { from, to } = editor.state.selection;
          setQ(editor.state.doc.textBetween(from, to, " ").trim());
          return true;
        }}
      >
        {(close) => (
          <div>
            <Field autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Chercher une définition…" />
            <div className="mt-1 max-h-56 overflow-y-auto">
              {list.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { editor.chain().focus().extendMarkRange("link").setLink({ href: `/cours/${t.slug}`, glossary: t.id, internal: null }).run(); close(); }}
                  className={`w-full text-left px-2 py-1.5 rounded text-sm hover:bg-white/5 ${t.id === current ? "text-orange-300" : "text-zinc-200"}`}
                >
                  {t.title}
                </button>
              ))}
              {!list.length && <div className="px-2 py-2 text-xs text-zinc-500">Aucune définition trouvée.</div>}
            </div>
            {current && (
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { editor.chain().focus().extendMarkRange("link").unsetLink().run(); close(); }} className="mt-1 w-full text-left px-2 py-1.5 rounded text-xs text-rose-400 hover:bg-rose-500/10">
                Retirer le lien vers le glossaire
              </button>
            )}
          </div>
        )}
      </ToolPopover>
    </div>
  );
}

/** Vidéo YouTube à l'endroit du curseur. */
export function VideoTool({ editor }) {
  const [url, setUrl] = useState("");
  return (
    <ToolPopover icon={<Youtube className="w-4 h-4" />} title="Insérer une vidéo YouTube" testid="rt-video" onOpen={() => setUrl("")}>
      {(close) => {
        const apply = () => {
          if (!isValidYoutubeUrl(url.trim())) { toast.error("Collez le lien d'une vidéo YouTube (youtube.com/watch?v=… ou youtu.be/…)"); return; }
          editor.chain().focus().setYoutubeVideo({ src: url.trim() }).run();
          close();
        };
        return (
          <div className="space-y-2">
            <div className="text-xs text-zinc-500">Lien de la vidéo YouTube</div>
            <Field autoFocus value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), apply())} placeholder="https://www.youtube.com/watch?v=…" />
            <div className="flex justify-end"><SmallBtn onClick={apply}>Insérer</SmallBtn></div>
          </div>
        );
      }}
    </ToolPopover>
  );
}

// Modèles de formules proposés (cliquer insère le modèle à la place du texte)
export const MATH_TEMPLATES = [
  { label: "Fraction", latex: "\\frac{a}{b}" },
  { label: "Puissance", latex: "x^{n}" },
  { label: "Indice", latex: "x_{t}" },
  { label: "Racine", latex: "\\sqrt{x}" },
  { label: "Somme", latex: "\\sum_{t=1}^{n} x_t" },
  { label: "Multiplié", latex: "a \\times b" },
  { label: "Intérêts composés", latex: "C_n = C_0 \\times (1 + r)^{n}" },
  { label: "Valeur actuelle nette", latex: "VAN = \\sum_{t=1}^{n} \\frac{F_t}{(1 + r)^{t}} - I_0" },
  { label: "Rendement", latex: "R = \\frac{P_1 - P_0 + D}{P_0}" },
];

function MathPreview({ latex, display }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    try {
      katex.render(latex || "\\;", ref.current, { displayMode: display, throwOnError: true });
      ref.current.dataset.error = "";
    } catch {
      ref.current.textContent = "Formule incomplète ou invalide";
      ref.current.dataset.error = "1";
    }
  }, [latex, display]);
  return <div ref={ref} className="min-h-[48px] grid place-items-center rounded-md bg-white/[0.03] border border-white/5 px-2 py-2 text-zinc-100 overflow-x-auto data-[error='1']:text-xs data-[error='1']:text-zinc-500" />;
}

/**
 * Formule : création, ou modification d'une formule existante (clic sur la formule dans le texte).
 * editing : null, ou { type: "block" | "inline", pos, latex } quand on clique sur une formule.
 */
export function MathTool({ editor, editing, setEditing }) {
  const [latex, setLatex] = useState("");
  const [display, setDisplay] = useState(true);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (editing) { setLatex(editing.latex || ""); setDisplay(editing.type === "block"); setOpen(true); }
  }, [editing]);
  const doClose = (v) => { setOpen(v); if (!v) setEditing(null); };
  return (
    <ToolPopover
      icon={<Sigma className="w-4 h-4" />}
      title="Insérer une formule"
      testid="rt-math"
      width={380}
      open={open}
      setOpen={doClose}
      onOpen={() => { setLatex(""); setDisplay(true); setEditing(null); }}
    >
      {(close) => {
        const apply = () => {
          const l = latex.trim();
          if (!l) return;
          if (editing) {
            if (editing.type === "block") editor.chain().focus().updateBlockMath({ latex: l, pos: editing.pos }).run();
            else editor.chain().focus().updateInlineMath({ latex: l, pos: editing.pos }).run();
          } else if (display) editor.chain().focus().insertBlockMath({ latex: l }).run();
          else editor.chain().focus().insertInlineMath({ latex: l }).run();
          close();
        };
        const remove = () => {
          if (editing.type === "block") editor.chain().focus().deleteBlockMath({ pos: editing.pos }).run();
          else editor.chain().focus().deleteInlineMath({ pos: editing.pos }).run();
          close();
        };
        return (
          <div className="space-y-2.5">
            <div className="text-xs text-zinc-500">{editing ? "Modifier la formule" : "Choisissez un modèle ou écrivez la formule"}</div>
            <div className="flex flex-wrap gap-1">
              {MATH_TEMPLATES.map((t) => (
                <button key={t.label} type="button" onClick={() => setLatex(t.latex)} className="px-2 py-1 rounded border border-white/10 text-[11px] text-zinc-300 hover:bg-white/5">{t.label}</button>
              ))}
            </div>
            <textarea
              autoFocus
              value={latex}
              onChange={(e) => setLatex(e.target.value)}
              rows={2}
              spellCheck={false}
              placeholder="Ex. : C_n = C_0 \times (1 + r)^{n}"
              className="w-full rounded-md bg-white/[0.04] border border-white/10 px-2.5 py-2 text-sm font-mono focus:outline-none focus:border-emerald-500/50"
            />
            <MathPreview latex={latex} display={display} />
            {!editing && (
              <div className="flex gap-1 text-xs">
                {[[true, "Sur sa propre ligne"], [false, "Dans la phrase"]].map(([v, l]) => (
                  <button key={l} type="button" onClick={() => setDisplay(v)} className={`px-2.5 py-1 rounded ${display === v ? "bg-white/10 text-white" : "text-zinc-400 hover:text-zinc-200"}`}>{l}</button>
                ))}
              </div>
            )}
            <div className="flex justify-between">
              {editing ? <SmallBtn variant="danger" onClick={remove}>Supprimer</SmallBtn> : <span />}
              <SmallBtn onClick={apply} disabled={!latex.trim()}>{editing ? "Mettre à jour" : "Insérer"}</SmallBtn>
            </div>
          </div>
        );
      }}
    </ToolPopover>
  );
}

// Couleurs de texte et de surlignage : uniquement celles du site
export const TEXT_COLORS = [
  { label: "Orange", value: "#F79837" },
  { label: "Vert", value: "#00BF63" },
  { label: "Violet", value: "#B175FF" },
  { label: "Rouge", value: "#F43F5E" },
  { label: "Gris", value: "#A1A1AA" },
];

export const HIGHLIGHTS = [
  { label: "Orange", value: "rgba(247,152,55,.28)" },
  { label: "Vert", value: "rgba(0,191,99,.25)" },
  { label: "Violet", value: "rgba(151,71,255,.28)" },
];

export function ColorTool({ editor }) {
  return (
    <ToolPopover icon={<Palette className="w-4 h-4" />} title="Couleur du texte" testid="rt-color" width={196}>
      {(close) => (
        <div>
          <div className="grid grid-cols-5 gap-1.5">
            {TEXT_COLORS.map((c) => (
              <button key={c.value} type="button" title={c.label} onMouseDown={(e) => e.preventDefault()} onClick={() => { editor.chain().focus().setColor(c.value).run(); close(); }} className="w-7 h-7 rounded-full border border-white/10" style={{ background: c.value }} />
            ))}
          </div>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { editor.chain().focus().unsetColor().run(); close(); }} className="mt-2 w-full text-xs text-zinc-400 py-1 rounded hover:bg-white/5">Couleur normale</button>
        </div>
      )}
    </ToolPopover>
  );
}

export function HighlightTool({ editor }) {
  return (
    <ToolPopover icon={<Highlighter className="w-4 h-4" />} title="Surligner" testid="rt-highlight" width={150}>
      {(close) => (
        <div>
          <div className="grid grid-cols-3 gap-1.5">
            {HIGHLIGHTS.map((c) => (
              <button key={c.value} type="button" title={c.label} onMouseDown={(e) => e.preventDefault()} onClick={() => { editor.chain().focus().toggleHighlight({ color: c.value }).run(); close(); }} className="h-7 rounded border border-white/10" style={{ background: c.value }} />
            ))}
          </div>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { editor.chain().focus().unsetHighlight().run(); close(); }} className="mt-2 w-full text-xs text-zinc-400 py-1 rounded hover:bg-white/5">Retirer</button>
        </div>
      )}
    </ToolPopover>
  );
}

/** Tableau : insertion (3 × 3 avec une ligne d'en-tête). */
export function TableInsertBtn({ editor }) {
  return (
    <ToolbarBtn testid="rt-table" title="Insérer un tableau" active={editor.isActive("table")} onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>
      <Table2 className="w-4 h-4" />
    </ToolbarBtn>
  );
}

/** Barre « Tableau » affichée quand le curseur est dans un tableau. */
export function TableBar({ editor }) {
  if (!editor.isActive("table")) return null;
  const b = (label, fn, Icon, danger) => (
    <button
      key={label}
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => fn(editor.chain().focus()).run()}
      className={`h-7 px-2 inline-flex items-center gap-1.5 rounded text-xs ${danger ? "text-rose-300 hover:bg-rose-500/10" : "text-zinc-300 hover:bg-white/5"}`}
    >
      {Icon && <Icon className="w-3.5 h-3.5" />}{label}
    </button>
  );
  return (
    <div className="flex flex-wrap items-center gap-0.5 px-2 py-1 border-b border-white/5 bg-emerald-500/[0.04]" data-testid="rt-table-bar">
      <span className="text-[10px] uppercase tracking-[0.15em] text-emerald-400 mr-2 inline-flex items-center gap-1"><FileText className="w-3 h-3" />Tableau</span>
      {b("Ligne au-dessus", (c) => c.addRowBefore(), Rows3)}
      {b("Ligne en dessous", (c) => c.addRowAfter(), Rows3)}
      {b("Colonne à gauche", (c) => c.addColumnBefore(), Columns3)}
      {b("Colonne à droite", (c) => c.addColumnAfter(), Columns3)}
      {b("Ligne d'en-tête", (c) => c.toggleHeaderRow())}
      <span className="w-px h-4 bg-white/10 mx-1" />
      {b("Supprimer la ligne", (c) => c.deleteRow(), null, true)}
      {b("Supprimer la colonne", (c) => c.deleteColumn(), null, true)}
      {b("Supprimer le tableau", (c) => c.deleteTable(), Trash2, true)}
    </div>
  );
}
